import { useState, createContext, useContext, type ReactNode } from 'react'
import { Outlet } from 'react-router-dom'
import { cn } from '@lib/utils'
import { Sidebar, SidebarNav, SidebarNavItem, SidebarDivider } from './Sidebar'
import { BottomTabBar } from './BottomTabBar'
import { ThemeToggle } from '@components/ui'

interface AppShellContextValue {
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
}

const AppShellContext = createContext<AppShellContextValue | null>(null)

/**
 * Hook to access AppShell state
 */
export function useAppShell() {
  const context = useContext(AppShellContext)
  if (!context) {
    throw new Error('useAppShell must be used within an AppShell')
  }
  return context
}

export interface AppShellProps {
  children?: ReactNode
  /** Sidebar content (defaults to navigation) */
  sidebar?: ReactNode
  /** Header content */
  header?: ReactNode
  /** Initial collapsed state */
  defaultCollapsed?: boolean
}

// Navigation icons as simple SVG components
const icons = {
  dashboard: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  businesses: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  keywords: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" />
      <line x1="16" y1="3" x2="14" y2="21" />
    </svg>
  ),
  insights: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
  notifications: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  ),
  reports: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  ),
  consultations: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  settings: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
}

/**
 * Default sidebar navigation
 */
function DefaultSidebar() {
  return (
    <Sidebar
      logo={
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary flex items-center justify-center">
            <span className="text-background font-bold">G</span>
          </div>
          <span className="font-bold text-text-primary">
            GeoRanker<span className="text-primary">.ai</span>
          </span>
        </div>
      }
      footer={
        <div className="flex items-center gap-2">
          <ThemeToggle size="sm" />
          <span className="text-caption text-text-muted hidden lg:inline">Theme</span>
        </div>
      }
    >
      <SidebarNav>
        <SidebarNavItem icon={icons.dashboard} label="Dashboard" href="/dashboard" />
        <SidebarNavItem icon={icons.businesses} label="Businesses" href="/businesses" />
        <SidebarNavItem icon={icons.keywords} label="Keywords" href="/keywords" />
      </SidebarNav>

      <SidebarDivider />

      <SidebarNav title="Features">
        <SidebarNavItem icon={icons.insights} label="AI Insights" href="/insights" />
        <SidebarNavItem icon={icons.reports} label="Reports" href="/reports" />
        <SidebarNavItem icon={icons.consultations} label="Consultations" href="/consultations" />
      </SidebarNav>

      <SidebarDivider />

      <SidebarNav>
        <SidebarNavItem icon={icons.notifications} label="Notifications" href="/notifications" badge={<span className="bg-error text-background text-xs px-1.5 py-0.5 font-bold">3</span>} />
        <SidebarNavItem icon={icons.settings} label="Settings" href="/settings" />
      </SidebarNav>
    </Sidebar>
  )
}

/**
 * Main application layout shell
 * Provides sidebar, header, and main content areas
 * When used with React Router, renders Outlet for nested routes
 */
export function AppShell({
  children,
  sidebar,
  header,
  defaultCollapsed = false,
}: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(defaultCollapsed)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Use default sidebar if none provided
  const sidebarContent = sidebar === undefined ? <DefaultSidebar /> : sidebar

  return (
    <AppShellContext.Provider
      value={{
        sidebarCollapsed,
        setSidebarCollapsed,
        sidebarOpen,
        setSidebarOpen,
      }}
    >
      <div className="min-h-screen bg-background">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-sidebar-backdrop bg-black/60 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Sidebar */}
        {sidebarContent && (
          <aside
            className={cn(
              'fixed top-0 left-0 z-sidebar h-full bg-surface border-r border-border',
              'transition-all duration-300 ease-out',
              // Mobile: slide in/out
              'lg:translate-x-0',
              sidebarOpen ? 'translate-x-0' : '-translate-x-full',
              // Desktop: collapse
              sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
            )}
          >
            {sidebarContent}
          </aside>
        )}

        {/* Main wrapper */}
        <div
          className={cn(
            'min-h-screen transition-all duration-300',
            sidebarContent && (sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64')
          )}
        >
          {/* Mobile header with menu button */}
          <header className="sticky top-0 z-header bg-surface border-b border-border lg:hidden">
            <div className="flex items-center justify-between h-14 px-4">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="p-2 text-text-muted hover:text-text-primary"
                aria-label="Open menu"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <span className="font-bold text-text-primary">
                GeoRanker<span className="text-primary">.ai</span>
              </span>
              <ThemeToggle size="sm" />
            </div>
          </header>

          {/* Custom header if provided */}
          {header && (
            <header className="sticky top-0 z-header bg-surface border-b border-border hidden lg:block">
              {header}
            </header>
          )}

          {/* Main content - renders children or Outlet for router */}
          <main id="main-content" className="flex-1 pb-20 lg:pb-0" tabIndex={-1}>
            {children || <Outlet />}
          </main>
        </div>

        {/* Mobile bottom tab bar */}
        <BottomTabBar notificationCount={3} />
      </div>
    </AppShellContext.Provider>
  )
}

export default AppShell
